/**
 * Case Management types for tracking case management events and program types.
 * Used for case manager workflows separate from visit-level reports.
 */

import { BaseEntity, DateField } from './base';

// ============================================================================
// PROGRAM TYPES
// ============================================================================

/**
 * Program types for categorizing cases.
 * Allows filtering and reporting by program.
 */
export type ProgramType =
  | 'supervised_visitation'
  | 'case_management'
  | 'juvenile_justice'
  | 'parenting_classes'
  | 'other';

export const PROGRAM_TYPE_LABELS: Record<ProgramType, string> = {
  supervised_visitation: 'Supervised Visitation',
  case_management: 'Case Management',
  juvenile_justice: 'Juvenile Justice',
  parenting_classes: 'Parenting Classes',
  other: 'Other',
};

// ============================================================================
// CASE MANAGEMENT EVENT TYPES
// ============================================================================

/**
 * Types of case management events that can be logged.
 */
export type CaseManagementEventType =
  | 'client_communication'
  | 'intervention'
  | 'referral'
  | 'court_cps_update'
  | 'other';

export const CASE_MANAGEMENT_EVENT_TYPE_LABELS: Record<CaseManagementEventType, string> = {
  client_communication: 'Client Communication',
  intervention: 'Intervention',
  referral: 'Referral',
  court_cps_update: 'Court/CPS Update',
  other: 'Other',
};

// ============================================================================
// CONTACT METHODS
// ============================================================================

/**
 * Contact methods for client communication events.
 * Required when eventType is 'client_communication'.
 */
export type ContactMethod =
  | 'in_person'
  | 'phone'
  | 'video'
  | 'email'
  | 'text'
  | 'other';

export const CONTACT_METHOD_LABELS: Record<ContactMethod, string> = {
  in_person: 'In Person',
  phone: 'Phone',
  video: 'Video Call',
  email: 'Email',
  text: 'Text Message',
  other: 'Other',
};

// ============================================================================
// CASE MANAGEMENT EVENT
// ============================================================================

/**
 * A case management event is a timeline item attached to a case.
 * Used for tracking communications, interventions, referrals, and updates
 * separate from visit-level documentation.
 */
export interface CaseManagementEvent extends BaseEntity {
  /** Account ID for tenant isolation */
  accountId: string;

  /** Case this event belongs to */
  caseId: string;

  /** Denormalized from case for efficient filtering */
  programType: ProgramType;

  /** Type of case management event */
  eventType: CaseManagementEventType;

  /** Contact method - required when eventType is 'client_communication' */
  contactMethod?: ContactMethod;

  /** Parent/child IDs involved in this event */
  participantIds?: string[];

  /** Short summary of the event (required) */
  summary: string;

  /** Detailed notes about the event (optional) */
  notes?: string;

  /** Outcome or result of the event (optional) */
  outcome?: string;

  /** Whether follow-up is needed */
  followUpNeeded: boolean;

  /** Date for follow-up if needed */
  followUpDate?: DateField;

  /** When the event occurred (not when it was logged) */
  occurredAt: DateField;

  /** User ID of who created this event */
  createdByUserId: string;

  /** Display name of who created this event (denormalized) */
  createdByName?: string;

  /** File attachments */
  attachments?: CaseManagementAttachment[];
}

/**
 * Attachment on a case management event.
 */
export interface CaseManagementAttachment {
  /** Unique file ID */
  fileId: string;

  /** Display name of the file */
  name: string;

  /** Download URL (may be signed/temporary) */
  url?: string;

  /** MIME type of the file */
  contentType?: string;

  /** File size in bytes */
  size?: number;
}

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

/**
 * Request to create a new case management event.
 */
export interface CreateCaseManagementEventRequest {
  /** Case this event belongs to */
  caseId: string;

  /** Type of event */
  eventType: CaseManagementEventType;

  /** Contact method (required for client_communication) */
  contactMethod?: ContactMethod;

  /** Parent/child IDs involved */
  participantIds?: string[];

  /** Short summary (required) */
  summary: string;

  /** Detailed notes */
  notes?: string;

  /** Outcome text */
  outcome?: string;

  /** Whether follow-up is needed */
  followUpNeeded?: boolean;

  /** ISO date string for follow-up date */
  followUpDate?: string;

  /** ISO date string for when event occurred */
  occurredAt: string;
}

/**
 * Request to update an existing case management event.
 */
export interface UpdateCaseManagementEventRequest {
  /** ID of event to update */
  eventId: string;

  /** New event type */
  eventType?: CaseManagementEventType;

  /** New contact method */
  contactMethod?: ContactMethod;

  /** New participant IDs */
  participantIds?: string[];

  /** New summary */
  summary?: string;

  /** New notes */
  notes?: string;

  /** New outcome */
  outcome?: string;

  /** New follow-up status */
  followUpNeeded?: boolean;

  /** New follow-up date (null to clear) */
  followUpDate?: string | null;

  /** New occurred at date */
  occurredAt?: string;
}

/**
 * Filter options for querying case management events.
 */
export interface CaseManagementEventsFilter {
  /** Filter by specific case */
  caseId?: string;

  /** Filter by program type */
  programType?: ProgramType;

  /** Filter by event type */
  eventType?: CaseManagementEventType;

  /** Filter events after this date (ISO string) */
  startDate?: string;

  /** Filter events before this date (ISO string) */
  endDate?: string;

  /** Filter by creator user ID */
  createdBy?: string;

  /** Pagination limit */
  limit?: number;

  /** Pagination offset */
  offset?: number;
}

/**
 * Aggregate statistics for case management events.
 */
export interface CaseManagementAggregates {
  /** Total number of events matching filter */
  totalEvents: number;

  /** Count of events by type */
  eventsByType: Record<CaseManagementEventType, number>;

  /** Count of events by contact method (for communication events) */
  eventsByContactMethod: Record<ContactMethod, number>;

  /** Number of unique cases with at least one event */
  uniqueCasesWithEvents: number;
}

/**
 * Response from the case management report endpoint.
 */
export interface CaseManagementReportResponse {
  /** Filtered events (paginated) */
  events: CaseManagementEvent[];

  /** Aggregate statistics */
  aggregates: CaseManagementAggregates;

  /** Total count (for pagination) */
  totalCount: number;

  /** Whether there are more results */
  hasMore: boolean;
}
